import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Box, Grid, Card, CardContent,
  TextField, Button, Alert, CircularProgress, Chip,
} from '@mui/material';
import {
  Church as ChurchIcon, CheckCircle, ArrowForward,
  GppGood as ShieldIcon, Groups as GroupsIcon,
  AutoGraph as GraphIcon, CloudDone as CloudIcon,
} from '@mui/icons-material';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';

const LIME = '#00F1A7';
const DARK = '#05080F';
const PURPLE = '#00F1A7';
const API = process.env.REACT_APP_API_URL || 'https://api.conectachurch.com.br';
const STRIPE_KEY = process.env.REACT_APP_STRIPE_KEY || '';

const BENEFICIOS = [
  { icon: <ChurchIcon />, title: 'Gestão completa', desc: 'Membros, secretaria, consolidação e discipulado em um só lugar' },
  { icon: <GroupsIcon />, title: '7 dias grátis', desc: 'Ambiente completo para testar com sua equipe sem compromisso' },
  { icon: <GraphIcon />, title: 'Relatórios inteligentes', desc: 'Dashboards prontos para apresentar ao pastor' },
  { icon: <CloudIcon />, title: '100% online', desc: 'Acesse de qualquer dispositivo, sem instalar nada' },
];

export default function CadastroIgreja() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planoS = searchParams.get('plano') || '';

  const [form, setForm] = useState({
    nome: '', cnpj: '', pastor_nome: '', email: '',
    telefone: '', endereco: '', bairro: '', cidade: '', estado: '', cep: '',
  });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim()) {
      setErro('Nome da igreja e email são obrigatórios');
      return;
    }
    setLoading(true);
    setErro('');
    try {
      const res = await fetch(`${API}/saas/igrejas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const igreja = await res.json();
      const igrejaId = igreja.id;

      // Se tem plano pago, redireciona para Stripe
      if (planoS && !['semente'].includes(planoS) && igrejaId) {
        const checkoutRes = await fetch(`${API}/saas/checkout?plano_id=${planoS}&igreja_id=${igrejaId}`, { method: 'POST' });
        const checkoutData = await checkoutRes.json();
        if (checkoutData.url) {
          window.location.href = checkoutData.url;
          return;
        }
      }
      setSuccess(true);
    } catch (err) {
      setErro('Erro ao enviar cadastro. Tente novamente.');
    }
    setLoading(false);
  };

  return (
    <>
      <PublicHeader />
      <Box sx={{ background: 'linear-gradient(150deg, #0A1D26, #05080F)', py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Chip
            label="PARA IGREJAS"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, mb: 3 }}
          />
          <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.8rem', md: '2.8rem' }, mb: 2 }}>
            Sua igreja a um passo<br />de uma gestão inteligente
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.1rem', maxWidth: 500, mx: 'auto' }}>
            Preencha o formulário. Nós avaliamos e liberamos seu trial gratuito em até 24h.
          </Typography>
        </Container>
      </Box>

      <Box sx={{ bgcolor: '#0A141C', py: { xs: 4, md: 8 }, minHeight: '60vh' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              {success ? (
                <Card sx={{ borderRadius: 3, p: 6, textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 72, color: '#00F1A7', mb: 2 }} />
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#E9F1EF', mb: 2 }}>
                    Cadastro enviado!
                  </Typography>
                  <Typography sx={{ color: '#93A7A3', mb: 4, lineHeight: 1.8 }}>
                    Recebemos seu cadastro. Nossa equipe avaliará e em até 24h você receberá
                    um email com acesso ao ambiente de teste gratuito por 7 dias.
                  </Typography>
                  <Button variant="contained" onClick={() => navigate('/')} sx={{ bgcolor: PURPLE, color: '#0B1200', fontWeight: 700, px: 4 }}>
                    Voltar ao início
                  </Button>
                </Card>
              ) : (
                <Card sx={{ borderRadius: 3, p: { xs: 3, md: 5 } }} elevation={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <ChurchIcon sx={{ color: PURPLE, fontSize: 32 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#E9F1EF' }}>
                      Cadastro da Igreja
                    </Typography>
                  </Box>

                  {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField required fullWidth label="Nome da Igreja" value={form.nome} onChange={handleChange('nome')} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="CNPJ" value={form.cnpj} onChange={handleChange('cnpj')} placeholder="00.000.000/0000-00" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Nome do Pastor" value={form.pastor_nome} onChange={handleChange('pastor_nome')} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField required fullWidth label="Email" type="email" value={form.email} onChange={handleChange('email')} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Telefone" value={form.telefone} onChange={handleChange('telefone')} placeholder="(00) 00000-0000" />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField fullWidth label="Endereço" value={form.endereco} onChange={handleChange('endereco')} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Bairro" value={form.bairro} onChange={handleChange('bairro')} />
                      </Grid>
                      <Grid item xs={6} md={5}>
                        <TextField fullWidth label="Cidade" value={form.cidade} onChange={handleChange('cidade')} />
                      </Grid>
                      <Grid item xs={3} md={2}>
                        <TextField fullWidth label="UF" value={form.estado} onChange={handleChange('estado')} inputProps={{ maxLength: 2 }} />
                      </Grid>
                      <Grid item xs={3} md={3}>
                        <TextField fullWidth label="CEP" value={form.cep} onChange={handleChange('cep')} placeholder="00000-000" />
                      </Grid>
                    </Grid>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      endIcon={<ArrowForward />}
                      sx={{ mt: 3, bgcolor: PURPLE, color: '#0B1200', fontWeight: 700, py: 1.8, fontSize: '1rem' }}
                    >
                      {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Enviar cadastro'}
                    </Button>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                      Ao enviar, você concorda com nossos termos de uso. Seus dados estão protegidos.
                    </Typography>
                  </Box>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'sticky', top: 24 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#E9F1EF' }}>
                  Por que escolher o ConectaChurch?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {BENEFICIOS.map((b, i) => (
                    <Card key={i} sx={{ borderRadius: 2, border: '1px solid rgba(0,241,167,0.12)', boxShadow: 'none' }}>
                      <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', '&:last-child': { pb: 2 } }}>
                        <Box sx={{ color: PURPLE, mt: 0.5 }}>{b.icon}</Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: '#E9F1EF', mb: 0.5 }}>{b.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{b.desc}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
